import { formatDate } from "@angular/common";
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { Address } from "@models/address/address";
import { AddressStatus } from "@models/address/address-status.enums";
import { Contact } from "@models/common/contact";
import { EntityType } from "@models/common/entity-type.enum";
import { Company } from '@models/companies/company';
import { Attendee } from "@models/documents/attendee";
import { ChangeAuthorisation, MinutesMeetingAttendee } from "@models/documents/change-authorisation";
import { Document } from "@models/documents/document";
import {
  DocumentationType,
  DocumentSelectionType,
  MeetingLocationType,
  MeetingOnlineType,
  MinutesMeetingPresence
} from "@models/documents/document-enums";
import { DocumentSelection } from "@models/documents/document-selection";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { IndividualData } from "@models/individuals/individual-data";
import { ParsedName } from "@models/individuals/parsed-name";
import { CompanyChangeOfficerType } from "@models/relationships/company-change-officer-type";
import { OfficerRelationshipDetails, Relationship } from "@models/relationships/relationship";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { SelectOption } from "@models/ui-models/select-options";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import {
  DocumentSelectionComponent
} from "@shared/components/documents-related/document-selection/document-selection.component";
import { isDocumentVisible } from "@shared/components/documents-related/document-selection/utils/is-document-visible";
import {
  AgDirectorMeetingStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-director-meeting-status/ag-director-meeting-status.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { InputPhoneNumberComponent } from "@shared/controls/input-phone-number/input-phone-number.component";
import { InputComponent } from "@shared/controls/input/input.component";
import { MultipleInputNameComponent } from "@shared/controls/multiple-input-name/multiple-input-name.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentsService } from "@shared/services/documents.service";
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { UsersApiService } from '@shared/services/users-api.service';
import { parseFullName } from "@shared/utils/parse-fullname";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { directorFullNameValidatorMsg, emailValidatorMsg, } from "@shared/validators/custom-form-validators-messages";
import { DatePickerFormValidators, DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { ColDef, GridApi, RowSelectionOptions } from "ag-grid-community";
import { observe, Observer } from "fast-json-patch";
import { deepClone } from "fast-json-patch/commonjs/core";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ToastrService } from "ngx-toastr";
import { catchError, filter, firstValueFrom, of, Subject } from "rxjs";
import {
  companyRegistrationDeclarationLabel,
  specialPurposeCompanyDeclaration, standardCompanyDeclaration
} from "../../../forms/asic-forms/company/company-registration/constants/company-registration-declaration";
import { isTrustEstablishmentDocumentType } from "../../../forms/asic-forms/trusts/constants/trust-establishment-texts";
import {
  IntegrationsEntitySearchControlComponent
} from "../../../forms/controls/integrations-entity-search-control/integrations-entity-search-control.component";
import { CompanyChangePartyName, CompanyRegistration } from "../../../forms/models/company";
import { CompanyChangeOfficer, ReasonOfCessation } from "../../../forms/models/company/company-change-officer.model";
import {
  AddressFormGroupComponent
} from "../../../forms/reusable-form-groups/address-form-group/address-form-group.component";
import { getDocumentationTypeOptions } from "../../../forms/utils/get-documentation-type-options";

@Component({
  selector: 'app-generate-documents-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NgxSkeletonLoaderModule,
    RadioComponent,
    InputComponent,
    DatePickerComponent,
    CheckboxComponent,
    SelectComponent,
    MultipleInputNameComponent,
    InputPhoneNumberComponent,
    DocumentSelectionComponent,
    AddressFormGroupComponent,
    IntegrationsEntitySearchControlComponent,
    ListGridClientSideComponent,
  ],
  providers: [],
  templateUrl: 'generate-documents-form.component.html',
  styleUrl: 'generate-documents-form.component.scss'
})
export class GenerateDocumentsFormComponent implements OnInit {
  @Input() document!: Document;
  @Input() company!: Company;
  @Input() saveAction$: Subject<boolean> | undefined;
  @Input() confirmAction$: Subject<boolean> | undefined;
  @Input() set declarationState(value: boolean) {
    this.declaration = value;
  }
  @Output() nextStep = new EventEmitter<boolean>();
  @Output() reloadDocument = new EventEmitter<boolean>();
  @Output() declarationStateChange = new EventEmitter<boolean>();

  private usersService = inject(UsersApiService);
  private documentsService = inject(DocumentsService);
  private documentsApiService = inject(DocumentsApiService);
  private organisationService = inject(OrganisationApiService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  #destroyRef: DestroyRef = inject(DestroyRef);

  readonly standardCompanyDeclaration = standardCompanyDeclaration;
  readonly specialPurposeCompanyDeclaration = specialPurposeCompanyDeclaration;
  readonly companyRegistrationDeclarationLabel = companyRegistrationDeclarationLabel;

  form = new FormGroup({
    address: AddressFormGroupComponent.defineForm(),
    directorNameList: new FormControl<string[] | null>([], [Validators.required, CustomFormValidators.directorNameValidator, CustomFormValidators.directorOneLetterNameValidator]),
    directorName: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.fullNameValidator]),
    phone: new FormControl<string | null>(null, [CustomFormValidators.phoneNumberValidator]),
    email: new FormControl<string | null>(null, [CustomFormValidators.emailValidator]),
  });
  companyRegistrationDeclarationAssentCheckbox = new FormControl<boolean>(false, [Validators.requiredTrue]);
  gridApi!: GridApi;
  colDefs: ColDef[] = [];

  formModel!: ChangeAuthorisation;
  formModelCopy!: ChangeAuthorisation;
  expandedAddressForm = false;


  documentTypeOptions: SelectOption[] = getDocumentationTypeOptions();

  meetingTypeOptions: SelectOption[] = [
    { label: 'Registered Address', value: MeetingLocationType.Registered },
    { label: 'Principal Address', value: MeetingLocationType.Principal },
    { label: 'Other address', value: MeetingLocationType.Other },
    { label: 'Phone', value: MeetingLocationType.Phone },
    { label: 'Online', value: MeetingLocationType.Online },
  ];

  meetingOnlineTypeOptions: SelectOption[] = [
    { label: 'Microsoft Teams', value: MeetingOnlineType.MicrosoftTeams },
    { label: 'Zoom', value: MeetingOnlineType.Zoom },
    { label: 'Google Meet', value: MeetingOnlineType.GoogleMeet }
  ];

  contactNameOptions: SelectOption[] = [];
  authorisingOfficeholdersOptions: SelectOption[] = [];
  directorOptions: SelectOption[] = [];
  chairOptions: SelectOption[] = [];
  RelationshipType = RelationshipType;

  minDateOfMeeting: NgbDateStruct = DatepickerHelper.getTodayStruct();
  maxDateOfMeeting: NgbDateStruct = DatepickerHelper.getNextNMonthStruct(2);

  declaration = false;
  authAgentDeclaration = false;

  DocumentationTypeEnum = DocumentationType;
  MeetingLocationTypeEnum = MeetingLocationType;
  readonly emailValidatorMsg = emailValidatorMsg;
  readonly directorFullNameValidatorMsg = directorFullNameValidatorMsg;
  readonly directorNameListCustomErrors = {
    startsWithDigit: 'The name must start with a letter',
    oneLetterPartName: 'The name must have more than one letter'
  };
  readonly attendeesRowSelection: RowSelectionOptions<any> = BaseListGridComponent.noCheckboxesRowSelection;

  individualsList: Relationship[] = [];
  attendeesList: Attendee[] = [];
  addressStatus: AddressStatus | undefined;
  isFormFilled = false;
  isRaForm = false;
  isCompanyRegistration = false;
  isSpecialPurposeCompanyRegistration = false;
  isTrustEstablishment = false;
  loading = false;
  documentSelection: DocumentSelection[] | undefined;
  documentsSelectionList: DocumentSelection[] = [];
  initialChangeAuthExists = false;
  customDatepickerErrors = DatePickerFormValidators.errorMessages;
  readonly raFormList = ['01', '04', '61', '63', '67', '71'];
  readonly authorisingOfficeholderEntitySearchEntityType = EntityType.Individual;

  observer!: Observer<Document>;

  constructor() {
    this.toastr.toastrConfig.positionClass = 'toast-top-right';
  }

  async ngOnInit(): Promise<void> {
    this.formModel = this.document?.changeAuthorisation ?? new ChangeAuthorisation();
    this.loading = true;
    await this.initForm();
    await this.getSelection();
    this.loading = false;
    this.listenHeaderButton();
    this.observer = observe(this.document);
    this.formModelCopy = structuredClone(this.formModel);

    this.isCompanyRegistration = this.document.changes.some((change) => change.$type == CompanyRegistration.$type);
    this.isSpecialPurposeCompanyRegistration = Boolean((this.document.changes.find((change) => change.$type == CompanyRegistration.$type) as CompanyRegistration | null)?.isSpecialPurpose);
    this.isTrustEstablishment = isTrustEstablishmentDocumentType(this.document.type);
  }

  private async getSelection(): Promise<void> {
    const documentSelection = await firstValueFrom(this.documentsApiService.getDocumentsSelection(this.document.documentId)
      .pipe(
        catchError((err) => {
          console.error(err);
          return of([]);
        }),
        takeUntilDestroyed(this.#destroyRef),
      ));

       this.documentsSelectionList = documentSelection.filter((doc, index, self) =>
        index === self.findIndex((d) => d.fileId === doc.fileId)
      );

    this.setDocumentTypeOptions();
    this.setDocumentsSelection();
  }

  setDocumentTypeOptions(): void {
    if (this.documentTypeIs362) {
      this.documentTypeOptions = getDocumentationTypeOptions().filter(({ value }) => value == DocumentationType.FormOnly);
      this.formModel.documentationType = this.documentTypeOptions[0].value as DocumentationType;
    }

    if(!this.documentsSelectionList?.length) {
      return;
    }

    const minutesNotExist = this.documentsSelectionList.findIndex(document => document.documentType === DocumentSelectionType.Minutes) === -1;
    const resolutionsNotExist = this.documentsSelectionList.findIndex(document => document.documentType === DocumentSelectionType.Resolution) === -1;

    if(minutesNotExist) {
      this.documentTypeOptions = this.documentTypeOptions.filter(o => o.value !== DocumentationType.Minutes);
      this.formModel.documentationType = DocumentationType.Resolution;
    }

    if(resolutionsNotExist) {
      this.documentTypeOptions = this.documentTypeOptions.filter(o => o.value !== DocumentationType.Resolution);
    }

    if(minutesNotExist && resolutionsNotExist) {
      this.documentTypeOptions = [];
      this.formModel.documentationType = DocumentationType.FormOnly;
    }
  }

  setDocumentsSelection(): void {
    if(!this.documentsSelectionList?.length) {
      return;
    }

    if(this.formModel.documentationType === DocumentationType.Minutes) {
      this.documentsSelectionList.forEach(document => {
        if(document.documentType === DocumentSelectionType.Minutes) {
          document.included = true;
        }
      });
    } else if (this.formModel.documentationType === DocumentationType.Resolution) {
      this.documentsSelectionList.forEach(document => {
        if(document.documentType === DocumentSelectionType.Resolution) {
          document.included = true;
        }
      });
    }

    this.documentSelection = this.initDocumentSelection(this.documentsSelectionList, this.document.changeAuthorisation);

    if(this.document?.changes?.length && this.document.changes[0].$type === 'CompanyChangeOfficer') {
      if((this.document.changes[0] as { cessationReason?: ReasonOfCessation })?.cessationReason !== ReasonOfCessation.Resignation) {
        this.documentSelection = this.documentSelection.filter(item => item.fileName !== 'Resignation Letter');
      }
    }

    this.documentSelection = this.documentSelection.filter(
      x => isDocumentVisible(x, this.formModel.documentationType)
    );
  }

  private initDocumentSelection(documentsSelection: DocumentSelection[], changeAuthorisation?: ChangeAuthorisation): DocumentSelection[] {
    return documentsSelection.map((documentSelection) => ({
      ...documentSelection,
      sequence: changeAuthorisation?.documentSelection?.selection?.find(d => d.fileName === documentSelection.fileName)?.sequence
      ?? documentSelection.sequence,
      included: changeAuthorisation?.documentSelection?.selection?.find(d => d.fileName === documentSelection.fileName)?.included
                ?? documentSelection.included
    }));
  }

  async initForm() {
    this.initialChangeAuthExists = !!this.document?.changeAuthorisation;
    if(!this.initialChangeAuthExists) {
      this.formModel.documentationType = DocumentationType.Minutes;
      this.formModel.meetingAddressType = MeetingLocationType.Registered;
    }

    this.isRaForm = this.raFormList.some(raType => this.document?.type === raType);
    if(this.documentTypeIs361 || this.documentTypeIs370 || this.isRaForm || this.documentTypeIs410b || this.documentTypeIs410f) {
      this.formModel.documentationType = DocumentationType.FormOnly;
      this.documentTypeOptions = this.documentTypeOptions.filter(o => o.value === DocumentationType.FormOnly);
    }

    if(this.documentTypeIs362) {
      this.meetingTypeOptions = this.meetingTypeOptions.filter(o => o.value !== MeetingLocationType.Registered && o.value !== MeetingLocationType.Principal);
    }

    this.individualsList = this.company?.officers ?? [];
    this.attendeesList = this.getAttendees();
    this.prefillAtendees();

    const authorisingPartyFullName = this.document.changeAuthorisation?.authorisingParty?.individualDataOverride?.fullName ?? '';
    const contact = this.document.changeAuthorisation?.authorisingParty?.contact;
    const directorName = (contact?.firstName ?? '') + ' ' + (contact?.lastName ?? '');

    this.form.patchValue({
      address: this.formModel.meetingAddress,
      directorNameList: [],
      directorName: authorisingPartyFullName ?? directorName.trim(),
      phone: contact?.phone ?? null,
      email: contact?.email ?? null,
    });


    this.form.controls.address.valueChanges.pipe(
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe((address) => {
      if (this.document.changeAuthorisation) {
        this.document.changeAuthorisation.meetingAddress = this.form.controls.address.getRawValue() as Address;
      }

      if (!address?.normalizedFullAddress) {
        this.documentsService.disabledHeaderBtn.set(true);
      } else {
        this.onDisableHeaderBtn();
      }
    });

    this.form.controls.directorNameList.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onDisableHeaderBtn());
    this.form.controls.directorName.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onDisableHeaderBtn());
    this.form.controls.phone.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onDisableHeaderBtn());
    this.form.controls.email.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onDisableHeaderBtn());

    this.setAllowedDateRange();
    this.setGridConfig();
    await this.setFormSelectOptions();

    const isNotAuthorisingId = !this.formModel.authorisingParty.relationshipId || this.formModel.authorisingParty.relationshipId === '00000000-0000-0000-0000-000000000000';
    if(isNotAuthorisingId && this.company?.signingContactId) {
      if(this.authorisingOfficeholdersOptions.some(o => (o.value as string) === this.company.signingContactId)) {
        this.formModel.authorisingParty.relationshipId = this.company.signingContactId;
      } else {
        this.formModel.authorisingParty.relationshipId = this.authorisingOfficeholdersOptions[0].value as string;
      }
    } else if(isNotAuthorisingId && this.authorisingOfficeholdersOptions?.length) {
      this.formModel.authorisingParty.relationshipId = this.authorisingOfficeholdersOptions[0].value as string;
    } else {
      if(this.authorisingOfficeholdersOptions.length && this.authorisingOfficeholdersOptions?.every(o => (o.value as string) !== this.formModel.authorisingParty.relationshipId)) {
        this.formModel.authorisingParty.relationshipId = this.authorisingOfficeholdersOptions[0].value as string;
      }
    }
    delete this.formModel.authorisingParty.details;
  }

  listenHeaderButton(): void {
    this.saveAction$?.pipe(
      filter(save => save),
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe(() => {
      void this.save();
      this.saveAction$?.next(false);
    });

    this.confirmAction$?.pipe(
      filter(confirm => confirm),
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe(() => {
      void this.confirm();
      this.confirmAction$?.next(false);
    });
  }

  async setFormSelectOptions(): Promise<void> {

    const users = await firstValueFrom(this.usersService.getAssignableUsersCached());
    this.contactNameOptions = users?.map(user => ({ label: user.fullName, value: user.id })) ?? [];

    if(!this.initialChangeAuthExists || !this.formModel.signingManager) {
      this.formModel.signingManager = this.organisationService.getCurrentUser()?.id ?? '';
    }

    this.authorisingOfficeholdersOptions = this.getAuthorisingOfficeholdersOptions()


    this.directorOptions = this.getOfficeholdersByType([RelationshipType.Director])
      .map((director) => ({ label: director.individualDataOverride!.fullNameLabel, value: director.relationshipId }));

    this.chairOptions = this.getChairpersonOptions()

    if(this.chairOptions.length) {
      if(!this.formModel?.chairperson) {
        this.formModel.chairperson = new Relationship();
        this.formModel.chairperson.relationshipId = this.chairOptions[0].value as string;
      }
    }

    if(this.directorOptions.length === 1 && this.documentTypeOptions.length) {
      this.documentTypeOptions = this.documentTypeOptions.filter(o => o.value !== this.DocumentationTypeEnum.Minutes);
      this.formModel.documentationType = this.documentTypeOptions[0].value as DocumentationType;
    }

    if(this.formModel.meetingAddressType !== undefined) {
      this.onMeetingAddressTypeSelect(this.formModel.meetingAddressType);
    }
  }

  setAllowedDateRange(): void {
    if(!this.formModel.meetingDate) {
      this.formModel.meetingDate = this.document?.changes[0]?.changeDate ?? new Date();
    }

    const dateOfEstablishment = this.document?.company?.dateOfEstablishment as string | undefined;
    if(dateOfEstablishment) {
      this.minDateOfMeeting = DatepickerHelper.getStructFromString(dateOfEstablishment);
      this.customDatepickerErrors = DatePickerFormValidators.errorMessagesWithDateOfEstablishmentErrorMessages;
    }
  }

  setGridConfig(): void {
    this.colDefs = [
      {
        headerName: 'Directors who attended the meeting',
        field: 'officer.individualDataOverride.fullName',
        width: 296,
        flex: 2,
        editable: false,
        sort: 'asc',
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Status',
        field: 'presence',
        width: 80,
        flex: 0.7,
        sortable: false,
        cellRenderer: AgDirectorMeetingStatusComponent,
        cellRendererParams: {
          onSelectDirectorMeetingStatus: this.onSelectDirectorMeetingStatus.bind(this),
        },
        cellClass: 'actions-button-cell',
      },
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));
  }

  onSelectDirectorMeetingStatus(attendee: Attendee): void {
    if (attendee.presence === MinutesMeetingPresence.Apology) {
      this.authorisingOfficeholdersOptions = this.authorisingOfficeholdersOptions.filter(o => o.value !== attendee.officer.relationshipId);
      this.formModel.authorisingParty.relationshipId = '';
    } else {
      this.authorisingOfficeholdersOptions = this.getAuthorisingOfficeholdersOptions()
        .filter((option) => {
          const relatedAttendee = this.attendeesList.find((attendee) => attendee.officer.relationshipId == option.value);
          return relatedAttendee?.presence !== MinutesMeetingPresence.Apology;
        });
    }
  }

  //tofo refactor, need to use individualId
  prefillAtendees() {
    const attendees = this.document.changeAuthorisation?.attendees ?? [];
    if(!attendees.length) {
      return;
    }

    this.attendeesList = this.attendeesList.map(x => {
      const attendee = attendees.find(y => y.individual.fullName.trim().toLowerCase() === x.officer.individualDataOverride?.fullName?.trim().toLowerCase());
      return (attendee === undefined || attendee.presence === MinutesMeetingPresence.Apology) ? x : { ...x, checked: true, presence: attendee.presence };
    });
  }

  onDocumentationTypeChange(): void {
    this.setDocumentsSelection();

    this.authAgentDeclaration = false;
    this.onDisableHeaderBtn();
  }

  onMeetingAddressTypeSelect(type: MeetingLocationType): void {
    if(type === MeetingLocationType.Other) {
      this.form.controls.address.patchValue(new Address());
      this.documentsService.disabledHeaderBtn.set(true);
      return;
    }

    if(type === MeetingLocationType.Registered) {
      this.form.controls.address.patchValue(this.company?.registeredAddress);
    } else if(type === MeetingLocationType.Principal) {
      this.form.controls.address.patchValue(this.company?.principalAddress);
    } else {
      this.form.controls.address.patchValue(new Address());
    }

    this.addressStatus = undefined;

    this.onDisableHeaderBtn();
  }

  onDataStatusChange(addressStatus: AddressStatus | undefined): void {
    this.addressStatus = addressStatus;
    if(addressStatus === AddressStatus.WARNING) {
      this.documentsService.disabledHeaderBtn.set(true);
    }
  }

  onChairPersonSelect(chairPersonId: string): void {
    if (!this.formModel.authorisingParty.relationshipId) {
      this.formModel.authorisingParty.relationshipId = chairPersonId;
    }

    this.onDisableHeaderBtn();
  }

  onDisableHeaderBtn(): void {
    this.documentsService.disabledHeaderBtn.set(!this.validateForm());
  }

  getOfficeholdersByType(relationshipTypes = [RelationshipType.Director, RelationshipType.Secretary, RelationshipType.AlternativeDirector]): Relationship[] {
    if (this.document.entityId) {
      const changesOfForm484A2 = this.document.changes.filter((change) => change.$type == CompanyChangePartyName.$type) as CompanyChangePartyName[];

      if (changesOfForm484A2.length) {
        return this.document.company?.currentOfficers
          .filter((officer) => relationshipTypes.includes(officer.type))
          .map((officer) => {
            const relatedChange = changesOfForm484A2.find((change) => change.relationshipIds.includes(officer.relationshipId));

            if (relatedChange) {
              officer.individualDataOverride = new IndividualData({
                ...officer.individualDataOverride,
                ...relatedChange
              });
              officer.individualDataOverride.fullName = IndividualData.buildFullNameWithMiddleLabel(officer.individualDataOverride);
            }

            return officer;
          });

      }

      return this.document.company?.currentOfficers.filter((officer) => relationshipTypes.includes(officer.type));
    }

    return [];
  }

  getAuthorisingOfficeholdersOptions(): SelectOption[] {
    let authorisingOfficeholders = this.getOfficeholdersByType([RelationshipType.Director]);

    authorisingOfficeholders = this.removeCessedOfficersFromList(authorisingOfficeholders);

    return authorisingOfficeholders
      .map((officer) => {
        const type = officer.type;
        const label = officer.individualDataOverride?.fullNameLabel + ` (${ RelationshipType[type] })`;
        return ({ label, value: officer.relationshipId });
      });
  }

  getAttendees() {
    let attendeesOfficers = this.getOfficeholdersByType([RelationshipType.Director]);

    attendeesOfficers = this.removeCessedOfficersFromList(attendeesOfficers);

    return attendeesOfficers
      .filter((relationship) => !relationship.end)
      .map(x => ({
        officer: x,
        checked: false,
        presence: MinutesMeetingPresence.PresentPhysically
      }));
  }

  private removeCessedOfficersFromList(officerList: Relationship[]): Relationship[] {
    // If the reason for the cessation is Death/Removal -
    // officer should be excluded from the
    // - 'authorising officeholders',
    // - list of attendees,
    // - 'chairperson'
    const ceaseDocumentChange = this.document.changes.find((change) => change.$type == CompanyChangeOfficer.$type) as CompanyChangeOfficer | undefined;
    if (
      ceaseDocumentChange
      && ceaseDocumentChange.actionType == CompanyChangeOfficerType.Removal
      && ceaseDocumentChange.cessationReason != null
      && [ReasonOfCessation.Removal, ReasonOfCessation.Death].includes(ceaseDocumentChange.cessationReason)
    ) {
      const ceaseOfficeholdersIds = ceaseDocumentChange.offices.map((officer) => officer.relationshipId);

      officerList = officerList
        .filter((officer) => !ceaseOfficeholdersIds.includes(officer.relationshipId));
    }
    return officerList;
  }

  getChairpersonOptions(): SelectOption[] {
    let chairpersonOfficers = this.getOfficeholdersByType([RelationshipType.Director]);

    chairpersonOfficers = this.removeCessedOfficersFromList(chairpersonOfficers);

    return chairpersonOfficers
      .map((chair) => {
        const type = chair.type;
        const label = chair.individualDataOverride?.fullNameLabel + ` (${ RelationshipType[type] })`;
        return ({ label, value: chair.relationshipId });
      });
  }

  getMinutesMeetingAttendeeFor362(directorNameList: string[]): MinutesMeetingAttendee[] {
    if (directorNameList?.length) {
      const attendeesArray: MinutesMeetingAttendee[] = [];
      const attendees: MinutesMeetingAttendee = {
        individual: new IndividualData({
          firstName: '',
          middleName1: '',
          middleName2: '',
          lastName: '',
          fullName: '',
          fullNameLabel: '',
          fullNameWithMiddle: '',
          formerName: new ParsedName(),
          din: '',
          address: new Address(),
          birthCity: '',
          birthCountry: '',
        })
      }

      directorNameList.forEach(name=> {
        const parsedName = parseFullName(name);
        attendees.individual.firstName = parsedName.firstName;
        attendees.individual.middleName1 = parsedName.middleName1;
        attendees.individual.middleName2 = parsedName.middleName2;
        attendees.individual.lastName = parsedName.lastName;

        attendeesArray.push(attendees);
      });

      return attendeesArray
    }

    return [];
  }

  private changeAuthorisationUpdate(): void {
    this.document.changeAuthorisation = this.formModel;

    if(
      this.formModel.meetingAddressType === MeetingLocationType.Registered
      || this.formModel.meetingAddressType === MeetingLocationType.Principal
      || this.formModel.meetingAddressType === MeetingLocationType.Other
    ) {
      this.document.changeAuthorisation.meetingAddress = this.form.controls.address.getRawValue() as Address;
    }

    if (this.documentTypeIs362) {
      const directorName = this.form.controls.directorName.value as string;
      const parsedName = parseFullName(directorName);
      const phone = this.form.controls.phone.value as string;
      const email = this.form.controls.email?.value as string;

      if (!this.company?.officers?.length) {
        if (!this.document.changeAuthorisation.authorisingParty) {
          this.document.changeAuthorisation.authorisingParty = new Relationship();
        }

        if (this.document.changeAuthorisation.authorisingParty.type === undefined) {
          this.document.changeAuthorisation.authorisingParty.type = RelationshipType.Director;
        }

        if (!this.document.changeAuthorisation.authorisingParty.individualDataOverride?.fullName?.trim()) {
          this.document.changeAuthorisation.authorisingParty.individualDataOverride = new IndividualData();
          this.document.changeAuthorisation.authorisingParty.details = new OfficerRelationshipDetails();
        }
      }

      const individualDataOverride = this.document.changeAuthorisation.authorisingParty.individualDataOverride!;

      individualDataOverride.firstName = parsedName.firstName;
      individualDataOverride.lastName = parsedName.lastName;
      individualDataOverride.middleName1 = parsedName.middleName1;
      individualDataOverride.middleName2 = parsedName.middleName2;

      this.document.changeAuthorisation.authorisingParty.contact = new Contact({
        firstName: parsedName.firstName,
        lastName: parsedName.lastName,
        phone,
        email
      });
    }

    if(this.formModel.documentationType == DocumentationType.Minutes) {
      if (this.documentTypeIs362 || (this.document.entityId === null && this.documentTypeIs484)) {
        const directorNameList = this.form.controls.directorNameList.value ?? [];
        this.formModel.attendees = this.getMinutesMeetingAttendeeFor362(directorNameList);
      } else {
        if(this.attendeesList.length) {
          this.formModel.attendees = this.attendeesList.map(x => new MinutesMeetingAttendee({
            individual: x.officer.individualDataOverride!,
            presence: x.presence
          }));
        }
      }

      this.document.changeAuthorisation.attendees = this.formModel.attendees;
    }

    if (this.formModel.meetingDate) {
      const meetingDate = this.formModel.meetingDate as Date | NgbDateStruct | string;
      const date = meetingDate instanceof Date ? meetingDate : typeof meetingDate === 'string' ? new Date(meetingDate) : DatepickerHelper.getDateFromStruct(meetingDate);
      const serverDateFormat = formatDate(date, 'yyyy-MM-dd', 'en-US');
      this.document.changeAuthorisation.meetingDate = serverDateFormat as unknown as Date;
    }

    if(this.formModel.authorisingParty.relationshipId) {
      const relationshipId = this.formModel.authorisingParty.relationshipId;
      const authorisingParty = this.getOfficeholdersByType()
        .find(officer => officer.relationshipId === relationshipId);

      if (authorisingParty && !this.documentTypeIs362) {
        this.document.changeAuthorisation.authorisingParty = deepClone(authorisingParty);
      }
    }

    if(this.formModel.chairperson?.relationshipId) {
      const relationshipId = this.formModel.chairperson.relationshipId;
      const chairperson = this.getOfficeholdersByType([RelationshipType.Director])
        .find(chair => chair.relationshipId === relationshipId);

      if (chairperson) {
        this.document.changeAuthorisation.chairperson = deepClone(chairperson);
      }
    }

    if(this.formModel.directorReplacedByAlternative?.relationshipId) {
      const relationshipId = this.formModel.directorReplacedByAlternative.relationshipId;
      const director = this.getOfficeholdersByType([RelationshipType.Director])
        .find(director => director.relationshipId === relationshipId);

      if (director) {
        this.document.changeAuthorisation.directorReplacedByAlternative = director;
      }
    }
  }

  checkFillForm(): void {
    const documentationType = this.document.changeAuthorisation?.documentationType;
    const isAuthorisingRelationshipId = this.document.changeAuthorisation?.authorisingParty?.relationshipId
      && this.document.changeAuthorisation?.authorisingParty?.relationshipId !== '00000000-0000-0000-0000-000000000000';

    if(documentationType === DocumentationType.Resolution || documentationType === DocumentationType.FormOnly) {
      if (this.document.changeAuthorisation?.signingManager && isAuthorisingRelationshipId) {
        this.isFormFilled = true;
      } else if (!this.company?.officers?.length && this.documentTypeIs362) {
        const contact = this.document.changeAuthorisation?.authorisingParty?.contact;
        const validContact = this.form.controls.directorName.valid && this.form.controls.phone.valid && this.form.controls.email?.valid;

        this.isFormFilled = Boolean(validContact
          && contact?.firstName
          && contact?.lastName
          && this.document.changeAuthorisation?.signingManager
          && this.document?.changeAuthorisation?.authorisingParty?.relationshipId
        );
      } else if ((this.documentTypeIs361 || this.documentTypeIs370 || this.isRaForm) && this.document.changeAuthorisation?.signingManager) {
        this.isFormFilled = true;
      }
    } else if (this.document.changeAuthorisation && documentationType === DocumentationType.Minutes) {
      if (!this.company?.officers?.length && this.documentTypeIs362) {
        const contact = this.document.changeAuthorisation?.authorisingParty?.contact;
        const isFormFilled = this.document.changeAuthorisation?.attendees?.length
          && this.document.changeAuthorisation?.meetingDate
          && this.document.changeAuthorisation?.signingManager
          && this.form.controls.directorName.valid
          && this.form.controls.phone.valid
          && this.form.controls.email?.valid;

        if(this.formModel.meetingAddressType === MeetingLocationType.Other) {
          if (this.document.changeAuthorisation.meetingAddress?.normalizedFullAddress && isFormFilled) {
            this.isFormFilled = true;
          }
        } else if (this.formModel.meetingAddressType === MeetingLocationType.Online && isFormFilled) {
          if (this.document.changeAuthorisation.meetingOnlineAddress) {
            this.isFormFilled = true;
          }
        } else if (this.formModel.meetingAddressType === MeetingLocationType.Phone && isFormFilled){
            this.isFormFilled = true;
        } else {
          this.isFormFilled = !!isFormFilled;
        }
      }

      if (this.document.changeAuthorisation.meetingDate && this.document.changeAuthorisation?.signingManager && isAuthorisingRelationshipId) {
        if (this.documentTypeIs362 || (this.document.entityId === null && this.documentTypeIs484)) {
          if(this.document.changeAuthorisation?.attendees?.length) {
            this.isFormFilled = true;
          }
        }

        if(this.getOfficeholdersByType([RelationshipType.Director]).length > 1) {
          if(this.formModel.chairperson?.relationshipId && this.document.changeAuthorisation.chairperson) {
            this.isFormFilled = true;
          }
        }

        if(this.formModel.meetingAddressType === MeetingLocationType.Other) {
          if (this.document.changeAuthorisation.meetingAddress?.normalizedFullAddress) {
            this.isFormFilled = true;
          }
        } else if (this.formModel.meetingAddressType === MeetingLocationType.Online) {
          if (this.document.changeAuthorisation.meetingOnlineAddress) {
            this.isFormFilled = true;
          }
        }
      }
    }
  }

  private validateForm(): boolean {
    if (((this.isCompanyRegistration && this.companyRegistrationDeclarationAssentCheckbox.valid) || this.isTrustEstablishment) && this.declaration) {
      return true;
    }

    this.changeAuthorisationUpdate();
    this.checkFillForm();

    if (!this.isFormFilled) {
      return false;
    }

    if ((this.documentTypeIs361 || this.isRaForm) && !this.authAgentDeclaration) {
      return false;
    }

    if (!this.declaration) {
      return false;
    }

    return true;
  }

  async save(): Promise<void> {
    try {
      this.documentsService.saveLoading.set(true);

      this.document.documentStatus = DocumentStatusEnum.Draft;
      await firstValueFrom(this.documentsApiService.updateAuthorisation(this.document.documentId, this.observer.object.changeAuthorisation!));
      this.toastr.success("Document saved", "Success");

      this.documentsService.saveLoading.set(false);

      await this.router.navigate(['/documents']);
    } catch (e) {
      this.toastr.error("Error happened", "Failed");
      console.log(e);
    }
  }

  async confirm(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }

    this.document.documentStatus = DocumentStatusEnum.SignaturePending;
    this.documentsService.patchLoading.set(true);

    if (this.isCompanyRegistration) {
      try {
        await firstValueFrom(this.documentsApiService.authoriseDocument(this.document.documentId, this.document.changeAuthorisation!));
        this.reloadDocument.emit(true);
      } catch (error) {
        this.toastr.error("Error happened", "Failed");
        console.warn('Error: ', error);
      }

      this.toastr.success("Document confirmed", "Success");
      this.nextStep.emit(true);
      this.documentsService.patchLoading.set(false);

      return;
    }


    try {
      if(!this.initialChangeAuthExists) {
        this.addChangeAuthToObservable();
      }

      if(!this.document.changeAuthorisation) {
        this.documentsService.patchLoading.set(false);
        this.toastr.error("Not initialized", "Change authorisation not initialized");
        return
      }

      this.document.changeAuthorisation.documentSelection = this.documentSelection?.length ? { selection: this.documentSelection } : this.document.changeAuthorisation.documentSelection;
      await firstValueFrom(this.documentsApiService.authoriseDocument(this.document.documentId, this.document.changeAuthorisation));
      this.reloadDocument.emit(true);

      this.toastr.success("Document confirmed", "Success");
      this.nextStep.emit(true);
      this.documentsService.patchLoading.set(false);
    } catch (e) {
      this.toastr.error("Error happened", "Failed");
      this.documentsService.patchLoading.set(false);
      console.log(e);
    }
  }

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
  }

  addChangeAuthToObservable(): void {
    const changeAuthorisation = deepClone(this.document.changeAuthorisation) as ChangeAuthorisation;
    this.document.changeAuthorisation = undefined;
    this.observer.unobserve();
    this.observer = observe(this.document);
    this.document.changeAuthorisation = changeAuthorisation;
  }

  onDocumentSelectionChange(documentSelections: DocumentSelection[]): void {
    this.documentSelection = documentSelections;
  }

  get documentTypeIs361(): boolean {
    return this.document?.type === '361' || this.document?.type === 'c:361';
  }

  get documentTypeIs362(): boolean {
    return this.document?.type === '362' || this.document?.type === 'c:362' || this.document?.type === 'c:362a';
  }

  get documentTypeIs370(): boolean {
    return this.document?.type === '370' || this.document?.type === 'c:370';
  }

  get documentTypeIs484(): boolean {
    return this.document?.type === '484' || this.document?.type === 'c:484';
  }

  get documentTypeIs410b(): boolean {
    return this.document?.type === '410b' || this.document?.type === 'c:410b';
  }

  get documentTypeIs410f(): boolean {
    return this.document?.type === '410f' || this.document?.type === 'c:410f';
  }

  declarationSelect(ev: boolean): void {
    this.declarationStateChange.emit(ev);
  }

  onDocumentTypeSelectionChange(): void {
    this.declaration = false;
  }
}
